import type { ReactNode } from "react";
import { HoverHintContent } from "@/components/ui/title-help";

import {
  mergeBreakdownLines,
  type ManufacturingScanBreakdownLine,
} from "./manufacturingReportingBreakdown";

function formatCount(value: number): string {
  return Math.trunc(value).toLocaleString();
}

function formatNetSecondsAsHoursMinutes(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const totalMinutes = Math.floor(safe / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}:${String(minutes).padStart(2, "0")}`;
}

function BreakdownList({ lines }: { lines: ManufacturingScanBreakdownLine[] }): JSX.Element | null {
  if (lines.length === 0) {
    return <p className="text-slate-500">None</p>;
  }
  return (
    <ul className="space-y-0.5">
      {lines.map((line) => (
        <li key={`${line.sku}:${line.kind}:${line.counted ? "counted" : "omitted"}`} className="flex justify-between gap-3">
          <span className="min-w-0 break-words">{line.sku}</span>
          <span className="shrink-0 tabular-nums">{formatCount(line.quantity)}</span>
        </li>
      ))}
    </ul>
  );
}

export function TimeTrackingStationHoverHint({
  stationKey,
  excludedNetSeconds,
  lines,
  children,
  className,
  layout = "stack",
}: {
  stationLabel: string;
  stationKey?: string;
  quantity: number;
  netSeconds: number;
  excludedNetSeconds: number;
  lines: ManufacturingScanBreakdownLine[];
  children: ReactNode;
  className?: string;
  layout?: "stack" | "row";
}): JSX.Element {
  const countedLines = mergeBreakdownLines(lines.filter((line) => line.counted));
  const omittedLines = mergeBreakdownLines(lines.filter((line) => !line.counted));
  const omittedQuantity = omittedLines.reduce((sum, line) => sum + line.quantity, 0);
  const usesLineItems =
    stationKey === "receiving" ||
    stationKey === "transfers" ||
    stationKey === "transfer_pack" ||
    countedLines.some((line) => line.kind === "line");
  const countedHeading = usesLineItems ? "Line items by SKU" : "Counted quantity";

  return (
    <HoverHintContent
      block={layout === "stack"}
      className={layout === "row" ? "flex w-full min-w-0 flex-row items-center justify-between" : className}
      hoverHint={
        <div className="min-w-[16rem] space-y-2">
          <div>
            <p className="font-medium text-slate-800">{countedHeading}</p>
            <BreakdownList lines={countedLines} />
          </div>
          {omittedLines.length > 0 ? (
            <div>
              <p className="font-medium text-slate-800">
                Omitted unserialized accessories {formatCount(omittedQuantity)} /{" "}
                {formatNetSecondsAsHoursMinutes(excludedNetSeconds)}
              </p>
              <BreakdownList lines={omittedLines} />
            </div>
          ) : null}
        </div>
      }
    >
      {children}
    </HoverHintContent>
  );
}
